/**
 * RotateHandle - draw.io-style circular rotation control.
 *
 * Rendered above a single selected node. On pointer-drag it computes the angle
 * from the node's center to the pointer and writes `data.rotation` (degrees).
 * Hold Shift to snap to 15° increments.
 *
 * NOTE: rotation is purely visual (applied to the node's content). Edges and
 * connection handles still attach to the node's un-rotated bounding box.
 */

import { useCallback, useRef } from 'react'
import { useReactFlow } from '@xyflow/react'
import { RotateCw } from 'lucide-react'

interface RotateHandleProps {
  nodeId: string
}

export default function RotateHandle({ nodeId }: RotateHandleProps) {
  const { setNodes, getNode, flowToScreenPosition } = useReactFlow()
  const draggingRef = useRef(false)

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.stopPropagation()
      e.preventDefault()
      draggingRef.current = true
      ;(e.target as Element).setPointerCapture?.(e.pointerId)

      const handleMove = (ev: PointerEvent) => {
        if (!draggingRef.current) return
        const node = getNode(nodeId)
        if (!node) return

        // Node center in screen coordinates (nodeOrigin is [0.5,0.5] in this
        // editor, so node.position is already the center in flow space).
        const width = node.measured?.width ?? node.width ?? 120
        const height = node.measured?.height ?? node.height ?? 120
        const centerFlow = {
          x: node.position.x,
          y: node.position.y,
        }
        // Guard for editors not using centered origin: fall back to top-left + half.
        const originIsCenter = true
        const cx = originIsCenter ? centerFlow.x : centerFlow.x + width / 2
        const cy = originIsCenter ? centerFlow.y : centerFlow.y + height / 2

        const centerScreen = flowToScreenPosition({ x: cx, y: cy })

        const dx = ev.clientX - centerScreen.x
        const dy = ev.clientY - centerScreen.y

        // Angle: 0deg points up (handle sits above the node). atan2 gives angle
        // from +x axis; offset by 90deg so straight-up = 0.
        let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90
        deg = ((deg % 360) + 360) % 360

        if (ev.shiftKey) {
          deg = Math.round(deg / 15) * 15
          deg = ((deg % 360) + 360) % 360
        }

        setNodes((nds) =>
          nds.map((n) =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, rotation: Math.round(deg) } }
              : n
          )
        )
      }

      const handleUp = () => {
        draggingRef.current = false
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
    },
    [nodeId, getNode, setNodes, flowToScreenPosition]
  )

  return (
    <div
      className="node-rotate-handle nodrag nopan"
      onPointerDown={onPointerDown}
      title="Drag to rotate (hold Shift to snap to 15°)"
      style={{
        position: 'absolute',
        top: -34,
        left: '50%',
        transform: 'translateX(-50%)',
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: '#3b82f6',
        border: '2px solid white',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        cursor: 'grab',
        zIndex: 50,
      }}
    >
      <RotateCw size={12} />
    </div>
  )
}
